"use client"

import { TableCell, Typography } from "@mui/material"
import { HighlightedText, Link } from "@tc/ui-dfe/components"
import { toPacificAucklandDateFormat } from "@tc/ui-dfe/utils/convertor"
import { ContentTableColumn, DataModel } from "@tc/ui-dfe/utils/types"
import { get } from "lodash"

const getOCACell = (
  data: DataModel,
  field: string,
  index: number,
  queryText: string,
) => (
  <TableCell key={`${index}-${field}`}>
    <HighlightedText
      text={data[field as keyof DataModel] ?? ""}
      highlight={queryText}
    />
  </TableCell>
)

export const getOcaColumns = (queryText: string): ContentTableColumn[] => [
  {
    title: "Certificate Number",
    style: { width: "clamp(180px, 200px, 250px)" },
    valueProp: "certificate_number",
    sort: {
      defaultSort: undefined,
      sortAttribute: "certificate_number_sort",
      ascLabel: "A to Z (Certificate Number)",
      descLabel: "Z to A (Certificate Number)",
      default: true,
    },
    customCell: (data: DataModel, index: number) => (
      <TableCell key={`${index}-certificate_number`}>
        <Link href={`/request/oca/${data.id}`} custom>
          <Typography variant="h6" color="forest">
            <HighlightedText
              text={data["certificate_number" as keyof DataModel] ?? ""}
              highlight={queryText}
            />
          </Typography>
        </Link>
      </TableCell>
    ),
  },
  {
    title: "Status",
    style: { width: "clamp(120px, 150px, 180px)" },
    valueProp: "status_label",
    sort: {
      defaultSort: undefined,
      sortAttribute: "status_label",
      ascLabel: "A to Z (Status)",
      descLabel: "Z to A (Status)",
      default: true,
    },
    customCell: (data: DataModel, index: number) =>
      getOCACell(data, "status_label", index, queryText),
  },
  {
    title: "Mode of Transport",
    style: { width: "clamp(140px, 160px, 200px)" },
    valueProp: "transport_mode",
    sort: {
      defaultSort: undefined,
      sortAttribute: "transport_mode",
      ascLabel: "A to Z (Mode of Transport)",
      descLabel: "Z to A (Mode of Transport)",
      default: true,
    },
    customCell: (data: DataModel, index: number) =>
      getOCACell(data, "transport_mode", index, queryText),
  },
  {
    title: "Exporter",
    style: { width: "clamp(150px, 180px, 220px)" },
    valueProp: "consignor_id",
    sort: {
      defaultSort: undefined,
      sortAttribute: "consignor_id",
      ascLabel: "A to Z (Exporter License Code)",
      descLabel: "Z to A (Exporter License Code)",
      default: true,
    },
    customCell: (data: DataModel, index: number) =>
      getOCACell(data, "consignor_id", index, queryText),
  },
  {
    title: "Inspection Port",
    style: { width: "clamp(150px, 180px, 220px)" },
    valueProp: "inspection_port_filter",
    sort: {
      defaultSort: undefined,
      sortAttribute: "inspection_port",
      ascLabel: "A to Z (Inspection Port)",
      descLabel: "Z to A (Inspection Port)",
      default: true,
    },
    customCell: (data: DataModel, index: number) =>
      getOCACell(data, "inspection_port_filter", index, queryText),
  },
  {
    title: "Importing Country",
    style: { width: "clamp(150px, 180px, 220px)" },
    valueProp: "country_name",
    sort: {
      defaultSort: undefined,
      sortAttribute: "country_name",
      ascLabel: "A to Z (Importing Country)",
      descLabel: "Z to A (Importing Country)",
      default: true,
    },
    customCell: (data: DataModel, index: number) =>
      getOCACell(data, "country_name", index, queryText),
  },
  {
    title: "Importer Name",
    style: { width: "clamp(150px, 180px, 220px)" },
    valueProp: "importer_name",
    sort: {
      defaultSort: undefined,
      sortAttribute: "importer_name",
      ascLabel: "A to Z (Importer Name)",
      descLabel: "Z to A (Importer Name)",
      default: true,
    },
    customCell: (data: DataModel, index: number) =>
      getOCACell(data, "importer_name", index, queryText),
  },
  {
    title: "Departure Date",
    style: { width: 160 },
    valueProp: "departure_ts",
    sort: {
      defaultSort: undefined,
      sortAttribute: "departure_ts",
      ascLabel: "Oldest to newest (Departure Date)",
      descLabel: "Newest to oldest (Departure Date)",
      default: true,
    },
    customCell: (data: DataModel) => (
      <TableCell sx={{ textAlign: "left" }} key={data.id + "departure_ts"}>
        <HighlightedText
          text={toPacificAucklandDateFormat(get(data, "departure_ts"))}
          highlight={queryText}
        />
      </TableCell>
    ),
  },
  {
    title: "Approved Date",
    style: { width: 160 },
    valueProp: "approved_at",
    sort: {
      sortAttribute: "approved_at",
      defaultSort: "desc",
      default: true,
      ascLabel: "Oldest to newest (Approved Date)",
      descLabel: "Newest to oldest (Approved Date)",
    },
    customCell: (data: DataModel) => (
      <TableCell sx={{ textAlign: "left" }} key={data.id + "approved_at"}>
        <HighlightedText
          text={toPacificAucklandDateFormat(get(data, "approved_at"))}
          highlight={queryText}
        />
      </TableCell>
    ),
  },
]
